// src/views/admin/ADDashboard.jsx
//
// CORRECTIF :
// - NOUVEAU : ajout de deux accès rapides "Mouvements caisse" et
//   "Scolarités" dans MODULES, pointant directement vers les sous-onglets
//   correspondants du module Caisse via ?tab=... (lu par ADCaisse.jsx).
// - NOUVEAU : chaque entrée de MODULES porte désormais un code
//   `permission` ; la grille "Accès rapide" est filtrée avec canSee()
//   avant le rendu.
// - NOUVEAU : couleurs alignées sur le thème Kamalpha (--pr / --or) et
//   les variables sémantiques (--gn, --am, --rd, --bl, --tl) définies
//   dans AdminLayout.jsx, au lieu de hex codés en dur.
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { usePermissions } from "../../context/PermissionsContext";
import { Chart, registerables } from "chart.js";
import ADSetupChecklist from "./ADSetupChecklist";

Chart.register(...registerables);
// Composant Modal générique
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ width: 650 }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Composant Carte Statistique
function StatCard({ label, value, color, icon, onClick, clickable = false, isText = false }) {
  const [hover, setHover] = useState(false);

  return (
    <div
      className={`stat-card ${clickable ? "stat-clickable" : ""}`}
      style={{
        borderLeftColor: color,
        transform: hover && clickable ? "translateY(-5px)" : "none",
        boxShadow: hover && clickable ? "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)" : "none",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
        <i className={`ti ${icon}`} />
      </div>
      <div className="stat-content">
        <div className="stat-value" style={{ fontSize: isText ? "20px" : "28px" }}>
          {value}
        </div>
      </div>
      <div className="stat-label">{label}</div>
      <div className="stat-progress" style={{ background: color }} />
    </div>
  );
}

// Composant Tableau de Section
function SectionTable({ title, icon, headers, rows }) {
  return (
    <div className="section-table">
      <div className="section-table-header">
        <div className="section-table-icon"><i className={`ti ${icon}`} /></div>
        <h2 className="section-table-title">{title}</h2>
      </div>
      <div className="table-wrapper">
        <table className="tbl">
          <thead>
            <tr>
              {headers.map(h => <th key={h}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? rows.map((r, i) => (
              <tr key={i}>
                {r.map((cell, j) => <td key={j}>{cell}</td>)}
              </tr>
            )) : (
              <tr><td colSpan={headers.length} style={{ textAlign: "center", color: "var(--h)", padding: "20px" }}>Aucune donnée disponible</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Modules "Accès rapide" — chaque entrée porte le code de permission requis
// pour apparaître ; voir NAV_PERMISSIONS dans AdminLayout.jsx pour la
// correspondance page ↔ code, et le catalogue PermissionAdmin (backend)
// pour la liste complète des codes existants.
// Couleurs alignées sur le thème Kamalpha (--pr, --or) et les variables
// sémantiques (--gn, --am, --rd, --bl, --tl) définies dans AdminLayout.jsx.
const MODULES = [
  { icon: "ti-users", label: "Élèves", path: "eleves", color: "var(--pr)", permission: "eleves.voir" },
  { icon: "ti-calendar-time", label: "Plannifications", path: "plannifications", color: "var(--tl)", permission: "plannifications.voir" },
  { icon: "ti-notebook", label: "Notes", path: "notes", color: "var(--am)", permission: "notes.voir" },
  { icon: "ti-file-certificate", label: "Bulletins", path: "bulletins", color: "var(--pr-lt)", permission: "bulletins.voir" },
  { icon: "ti-fingerprint", label: "Pointage", path: "pointage", color: "var(--rd)", permission: "pointage.voir" },
  { icon: "ti-id", label: "Cartes", path: "cartes", color: "var(--or)", permission: "cartes.voir" },
  // Liens directs vers les sous-onglets du module Caisse (deep-link via
  // ?tab=..., lu par ADCaisse.jsx pour ouvrir directement le bon onglet —
  // cf. ADCaisseSituationVersements.jsx qui gère en plus ?inscription=<id>).
  { icon: "ti-cash", label: "Mouvements caisse", path: "caisse?tab=caisses", color: "var(--gn)", permission: "caisse.voir" },
  { icon: "ti-report-money", label: "Scolarités", path: "caisse?tab=situation", color: "var(--bl)", permission: "caisse.voir" },
  { icon: "ti-messages", label: "Messages", path: "messages", color: "var(--tl)", permission: "messagerie.voir" },
  { icon: "ti-bell-ringing", label: "Notifications", path: "notifications", color: "var(--or-dk)", permission: "notifications.voir" },
];

// Palette du donut "Présents par classe" : nombre de classes variable,
// besoin de teintes distinctes indépendantes du thème de marque —
// inchangée volontairement.
const DONUT_COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82ca9d",
  "#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57", "#ff9ff3"
];

const KEY_MAP = {
  "Élève": "eleve", "Classe": "classe", "Heure": "heure", "Statut": "statut",
  "Parent": "parent", "Offre": "offre", "Date fin": "date_fin",
  "Jours restants": "jours_restants", "Téléphone": "telephone",
  "Enfant": "enfant"
};

function getModalConfig(stats, type) {
  const configs = {
    presents: {
      title: "Élèves Présents Aujourd'hui",
      data: stats.details_presents || [],
      headers: ["Élève", "Classe", "Heure", "Statut"]
    },
    retards: {
      title: "Élèves en Retard Aujourd'hui",
      data: stats.details_retards || [],
      headers: ["Élève", "Classe", "Heure", "Statut"]
    },
    absents: {
      title: "Élèves Absents Aujourd'hui",
      data: stats.details_absents || [],
      headers: ["Élève", "Classe", "Heure", "Statut"]
    },
    abonnements: {
      title: "Abonnements Expirant Sous 7 Jours",
      data: stats.details_abonnements || [],
      headers: ["Parent", "Offre", "Date fin", "Jours restants", "Téléphone"]
    }
  };
  return configs[type] || { title: "", data: [], headers: [] };
}

// Lit une variable CSS custom depuis le DOM (pour que Chart.js suive votre thème clair/sombre)
function cssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return v && v.trim() ? v.trim() : fallback;
}

export default function ADDashboard({ slug }) {
  const navigate = useNavigate();
  const go = (path) => navigate(`/${slug}/admin/${path}`);
  const { canSee } = usePermissions();

  const [stats, setStats] = useState(null);
  const [justifStats, setJustifStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modal, setModal] = useState(null);

  const toast = useToastHook();
  const { getDashboardStats, getJustificationsStats } = useAdminService();

  // Refs pour les graphiques Chart.js
  const barCanvasRef = useRef(null);
  const pieCanvasRef = useRef(null);
  const barChartInstance = useRef(null);
  const pieChartInstance = useRef(null);

  useEffect(() => {
    if (slug) load(true);
  }, [slug]);

  // Création / mise à jour des graphiques Chart.js dès que les stats changent
  useEffect(() => {
    if (stats?.stats_7_jours && stats?.taux_par_classe) {
      createCharts();
    }
    return () => {
      barChartInstance.current?.destroy();
      pieChartInstance.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  const load = async (initial = false) => {
    initial ? setLoading(true) : setRefreshing(true);
    try {
      const [data, justifData] = await Promise.all([
        getDashboardStats(slug),
        getJustificationsStats(slug).catch(() => null),
      ]);
      setStats(data);
      setJustifStats(justifData);
    } catch (error) {
      toast.error("Impossible de charger le tableau de bord");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const createCharts = () => {
    barChartInstance.current?.destroy();
    pieChartInstance.current?.destroy();

    const textColor = cssVar("--m", "#64748b");
    const gridColor = cssVar("--b", "#e2e8f0");

    // Graphique en barres empilées : présence sur 7 jours
    if (barCanvasRef.current) {
      barChartInstance.current = new Chart(barCanvasRef.current, {
        type: "bar",
        data: {
          labels: stats.stats_7_jours.map(j => j.date),
          datasets: [
            { label: "Présents", data: stats.stats_7_jours.map(j => j.presents), backgroundColor: cssVar("--gn", "#10b981"), borderRadius: 4, maxBarThickness: 28 },
            { label: "Retards", data: stats.stats_7_jours.map(j => j.retards), backgroundColor: cssVar("--am", "#f59e0b"), borderRadius: 4, maxBarThickness: 28 },
            { label: "Absents", data: stats.stats_7_jours.map(j => j.absents), backgroundColor: cssVar("--rd", "#ef4444"), borderRadius: 4, maxBarThickness: 28 },
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: cssVar("--s", "#ffffff"),
              titleColor: cssVar("--t", "#0f172a"),
              bodyColor: cssVar("--t", "#0f172a"),
              borderColor: gridColor,
              borderWidth: 1,
              padding: 10,
              boxPadding: 4,
              usePointStyle: true,
            }
          },
          scales: {
            x: { stacked: true, grid: { display: false }, ticks: { color: textColor, font: { size: 11 } } },
            y: { stacked: true, beginAtZero: true, grid: { color: gridColor }, ticks: { color: textColor, precision: 0 } }
          }
        }
      });
    }

    // Graphique en donut : répartition des présents par classe
    if (pieCanvasRef.current) {
      pieChartInstance.current = new Chart(pieCanvasRef.current, {
        type: "doughnut",
        data: {
          labels: stats.taux_par_classe.map(c => c.classe),
          datasets: [{
            data: stats.taux_par_classe.map(c => c.presents),
            backgroundColor: DONUT_COLORS,
            borderWidth: 2,
            borderColor: cssVar("--s", "#ffffff"),
            hoverOffset: 8,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "62%",
          plugins: {
            legend: {
              position: "bottom",
              labels: { color: textColor, boxWidth: 10, padding: 12, font: { size: 12 } }
            },
            tooltip: {
              backgroundColor: cssVar("--s", "#ffffff"),
              titleColor: cssVar("--t", "#0f172a"),
              bodyColor: cssVar("--t", "#0f172a"),
              borderColor: gridColor,
              borderWidth: 1,
              padding: 10,
              callbacks: {
                label: (ctx) => {
                  const c = stats.taux_par_classe[ctx.dataIndex];
                  return ` ${ctx.label}: ${c.presents}/${c.total} élèves (${c.taux}%)`;
                }
              }
            }
          }
        }
      });
    }
  };

  const openDetails = (type) => setModal(getModalConfig(stats, type));
  const closeModal = () => setModal(null);

  if (loading) return <Loader />;
  if (!stats) return null;

  const abonnementsExpirant = stats.abonnements_expirant ?? (stats.details_abonnements?.length || 0);
  const justifEnAttente = justifStats?.en_attente ?? justifStats?.total_en_attente ?? 0;
  const totalPresentsClasses = stats.taux_par_classe.reduce((s, c) => s + c.presents, 0) || 0;

  const alertes = [
    stats.retards > 0 && { icon: "ti-clock", label: `${stats.retards} retard(s) aujourd'hui`, count: stats.retards, path: "pointage" },
    justifEnAttente > 0 && { icon: "ti-file-alert", label: "Justificatifs en attente", count: justifEnAttente, path: "pointage" },
    abonnementsExpirant > 0 && { icon: "ti-credit-card", label: "Abonnements expirant sous 7 jours", count: abonnementsExpirant, path: "abonnements" },
    stats.messages_non_lus > 0 && { icon: "ti-mail", label: "Messages non lus", count: stats.messages_non_lus, path: "messages" },
  ].filter(Boolean);

  // Statistiques pour les cartes — couleurs alignées sur le thème
  // (variables sémantiques --gn/--am/--rd + identité --pr/--or)
  const statsCards = [
    { label: "Présents aujourd'hui", value: stats.presents || 0, color: "var(--gn)", icon: "ti-circle-check", type: "presents" },
    { label: "En retard aujourd'hui", value: stats.retards || 0, color: "var(--am)", icon: "ti-clock", type: "retards" },
    { label: "Absents aujourd'hui", value: stats.absents || 0, color: "var(--rd)", icon: "ti-circle-x", type: "absents" },
    { label: "Total élèves", value: stats.total_eleves || 0, color: "var(--pr)", icon: "ti-users" },
    { label: "Messages non lus", value: stats.messages_non_lus || 0, color: "var(--or-dk)", icon: "ti-mail" },
    { label: "Abonnements expirant", value: abonnementsExpirant || 0, color: "var(--am)", icon: "ti-credit-card", type: "abonnements" },
  ];

  return (
    <div>
<ADSetupChecklist
  slug={slug}
  onNavigateTab={(tab) => navigate(`/${slug}/admin/parametres?tab=${tab}`)}
/>
      {/* Barre d'actions */}
      <div className="sb2" style={{ justifyContent: "flex-end" }}>
        <button className="bsec" onClick={() => load(false)} disabled={refreshing}>
          <i className={`ti ti-refresh${refreshing ? " dash-spin" : ""}`} />
          {refreshing ? "Actualisation..." : "Actualiser"}
        </button>
      </div>

      {/* Cartes de statistiques */}
      <div className="stats-grid">
        {statsCards.map(c => (
          <StatCard
            key={c.label}
            label={c.label}
            value={c.value}
            color={c.color}
            icon={c.icon}
            onClick={() => c.type && openDetails(c.type)}
            clickable={!!c.type}
          />
        ))}
      </div>

      {/* Accès rapide + à traiter */}
      <div className="quick-row">
        <div className="quick-modules">
          <div className="section-label">Accès rapide</div>
          <div className="modules-grid">
            {MODULES.filter(m => canSee(m.permission)).map((m) => (
              <button key={m.path} type="button" className="module-card" onClick={() => go(m.path)}>
                <div className="module-icon" style={{ background: m.color }}>
                  <i className={`ti ${m.icon}`} />
                </div>
                <span className="module-label">{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="quick-alerts">
          <div className="section-label">À traiter</div>
          <div className="alert-panel">
            {alertes.length > 0 ? (
              alertes.map((a, i) => (
                <button key={i} className="alert-row" onClick={() => go(a.path)}>
                  <i className={`ti ${a.icon}`} />
                  <span className="alert-label">{a.label}</span>
                  <span className="alert-count">{a.count}</span>
                </button>
              ))
            ) : (
              <div className="alert-empty-ok"><i className="ti ti-circle-check" /> Rien en attente</div>
            )}
          </div>
        </div>
      </div>

      {/* Graphiques interactifs (Chart.js) */}
      <div className="charts-grid">
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Présence sur 7 jours</h3>
          </div>
          <div className="chart-canvas">
            <canvas ref={barCanvasRef} />
          </div>
          <div className="chart-legend">
            <span><span className="legend-dot present" /> Présents</span>
            <span><span className="legend-dot late" /> Retards</span>
            <span><span className="legend-dot absent" /> Absents</span>
          </div>
        </div>

        <div className="pie-chart-container">
          <h3 className="chart-title">Présents par classe</h3>
          <div className="chart-canvas">
            {stats.taux_par_classe.length === 0 ? (
              <div style={{ color: "var(--h)", textAlign: "center", padding: "20px" }}>
                Aucune donnée disponible
              </div>
            ) : (
              <div className="pie-chart-wrapper">
                <canvas ref={pieCanvasRef} />
                <div className="pie-total-badge">
                  <div className="pie-total">{totalPresentsClasses}</div>
                  <div className="pie-sub">présents</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tableaux de données */}
      <div className="tables-grid">
        <SectionTable
          title="Taux de présence par classe"
          icon="ti-chart-line"
          headers={["Classe", "Présents", "Total", "Taux"]}
          rows={stats.taux_par_classe?.map(c => [c.classe, c.presents, c.total, `${c.taux}%`]) || []}
        />
        <SectionTable
          title="Activités récentes"
          icon="ti-calendar"
          headers={["Type", "Description", "Classe", "Heure"]}
          rows={stats.activites_recentes?.map(a => [
            a.type === "presence" ? "Présence" : "Message",
            a.description,
            a.classe || "-",
            new Date(a.heure).toLocaleTimeString()
          ]) || []}
        />
      </div>

      {/* Modal de détail */}
      {modal && (
        <Modal title={modal.title} onClose={closeModal}>
          {modal.data.length === 0 ? (
            <div style={{ color: "var(--h)", textAlign: "center", padding: "20px" }}>Aucune donnée disponible</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="tbl">
                <thead>
                  <tr>
                    {modal.headers.map(h => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {modal.data.map((item, i) => (
                    <tr key={i}>
                      {modal.headers.map(h => {
                        const key = KEY_MAP[h] || h.toLowerCase();
                        return <td key={h}>{item[key] || "-"}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {/* Styles */}
      <style>{`
        /* === CARTES STATISTIQUES === */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .stat-card {
          background: var(--s);
          border-radius: 10px;
          padding: 18px 20px 14px;
          border-left: 4px solid var(--pr);
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          position: relative;
          transition: all 0.25s ease;
          display: flex;
          flex-direction: column;
        }

        .stat-clickable {
          cursor: pointer;
        }

        .stat-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 10px;
        }

        .stat-content {
          display: flex;
          align-items: baseline;
          gap: 6px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: var(--t);
          line-height: 1.2;
        }

        .stat-label {
          font-size: 14px;
          color: var(--m);
          margin-top: 2px;
        }

        .stat-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          width: 100%;
          border-radius: 0 0 10px 10px;
          opacity: 0.2;
        }

        /* === ACCÈS RAPIDE + À TRAITER === */
        .quick-row {
          display: grid;
          grid-template-columns: 2fr 1fr;
          align-items: start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .section-label {
          font-size: 12.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--m);
          margin-bottom: 10px;
        }

        .modules-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
          gap: 10px;
        }

        .module-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          background: var(--s);
          border: 0.5px solid var(--b);
          border-radius: 10px;
          padding: 16px 8px;
          cursor: pointer;
          font-family: inherit;
          transition: all .2s;
        }
        .module-card:hover {
          border-color: var(--pr);
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
          transform: translateY(-2px);
        }

        .module-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
        }

        .module-label {
          font-size: 12.5px;
          font-weight: 600;
          color: var(--t);
          text-align: center;
        }

        .alert-panel {
          background: var(--s);
          border: 0.5px solid var(--b);
          border-radius: 10px;
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          box-sizing: border-box;
        }

        .alert-row {
          display: flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: none;
          border-radius: 6px;
          padding: 8px 8px;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 600;
          color: var(--t);
          width: 100%;
        }
        .alert-row:hover { background: var(--s2); }
        .alert-row i { color: var(--rd); font-size: 15px; }
        .alert-label { flex: 1; }
        .alert-count {
          background: var(--rd);
          color: #fff;
          border-radius: 10px;
          padding: 1px 7px;
          font-size: 10.5px;
          font-weight: 700;
        }

        .alert-empty-ok {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--gn);
          font-weight: 600;
          font-size: 12.5px;
          padding: 8px;
        }

        /* === GRAPHIQUES === */
        .charts-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }

        .chart-container, .pie-chart-container {
          background: var(--s);
          border-radius: 10px;
          padding: 16px 18px;
          border: 0.5px solid var(--b);
        }

        .chart-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--t);
          margin: 0 0 12px 0;
        }

        .chart-canvas {
          min-height: 220px;
          height: 220px;
          position: relative;
        }

        .chart-legend {
          display: flex;
          justify-content: center;
          gap: 20px;
          margin-top: 12px;
          font-size: 13px;
          color: var(--m);
        }

        .legend-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-right: 4px;
        }
        .legend-dot.present { background: var(--gn); }
        .legend-dot.late { background: var(--am); }
        .legend-dot.absent { background: var(--rd); }

        /* Donut Chart.js + badge central */
        .pie-chart-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .pie-total-badge {
          position: absolute;
          top: 38%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }

        .pie-total {
          font-size: 22px;
          font-weight: 700;
          color: var(--t);
          line-height: 1;
        }

        .pie-sub {
          font-size: 11px;
          color: var(--m);
        }

        /* === TABLEAUX === */
        .tables-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .section-table {
          background: var(--s);
          border-radius: 10px;
          border: 0.5px solid var(--b);
          overflow: hidden;
        }

        .section-table-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-bottom: 0.5px solid var(--b);
        }

        .section-table-icon {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: var(--pr-bg);
          color: var(--pr);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .section-table-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--t);
          margin: 0;
        }

        .table-wrapper {
          padding: 0;
          overflow-x: auto;
        }

        .table-wrapper .tbl {
          font-size: 14px;
        }
        .table-wrapper .tbl th,
        .table-wrapper .tbl td {
          padding: 8px 14px;
        }

        /* === MODALE === */
        .modal-box .tbl {
          font-size: 14px;
        }
        .modal-box .tbl th,
        .modal-box .tbl td {
          padding: 8px 12px;
        }

        /* === ANIMATIONS === */
        @keyframes dashspin {
          to { transform: rotate(360deg); }
        }
        .dash-spin {
          animation: dashspin 0.7s linear infinite;
        }

        /* === RESPONSIVE === */
        @media (max-width: 992px) {
          .charts-grid { grid-template-columns: 1fr; }
          .tables-grid { grid-template-columns: 1fr; }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .quick-row { grid-template-columns: 1fr; }
        }

        @media (max-width: 576px) {
          .stats-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}